import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
} from "react-query";
import {
  fetchFolder,
  uploadFile,
  deleteFile,
  deleteFileById,
  moveFile,
  renameFile,
} from "@modules/box/client";
import request from "@common/request";
import { BoxFile, BoxFolder } from "@modules/box/types";
import { AxiosError } from "axios";

const retryWithExpiredTokenRefresh = (
  failureCount: number,
  error: AxiosError,
  queryClient: QueryClient
) => {
  if (error) {
    if (error.message.match(/409/)) {
      // Do not retry if we get a 409.
      return false;
    }
    const message = error.message;
    // Unfortunately, the axios response is a generic NetworkError with status === undefined.
    // The network tab shows a 401 but for some reason this isn't passed through axios...
    if (message.match(/Network Error/i) || error?.response?.status === 401) {
      console.debug("Invalidating Box JWT and retrying...");
      queryClient.invalidateQueries(["docVaultJwt"]);
    }
  }
  return failureCount < 3; // max 3 retries
};

// Box access tokens last 60 minutes. Going stale at 50 lets mount, window
// focus and the folder fetch below each swap in a new one before expiry.
const DOC_VAULT_JWT_STALE_TIME = 50 * 60 * 1000;

const fetchDocVaultJwt = () =>
  request<{
    accessToken: string;
    rootFolderId: string;
    privateRootFolderId: string;
  }>("POST", "/api/docvault/token");

// Read the token on every attempt. Retries reuse the same function, so a token
// captured at render would stay expired after retryWithExpiredTokenRefresh.
const getDocVaultAccessToken = async (queryClient: QueryClient) => {
  const { accessToken } = await queryClient.fetchQuery(
    "docVaultJwt",
    fetchDocVaultJwt,
    { staleTime: DOC_VAULT_JWT_STALE_TIME }
  );
  return accessToken;
};

const useDocVaultJwt = () => {
  return useQuery("docVaultJwt", fetchDocVaultJwt, {
    staleTime: DOC_VAULT_JWT_STALE_TIME,
    cacheTime: DOC_VAULT_JWT_STALE_TIME,
  });
};

const useFolder = ({
  accessToken,
  folderId,
}: {
  folderId: string;
  accessToken?: string;
}) => {
  const queryClient = useQueryClient();
  return useQuery(
    ["folders", folderId],
    async () =>
      fetchFolder({
        accessToken: await getDocVaultAccessToken(queryClient),
        folderId,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!accessToken && !!folderId,
      retry: (failureCount, error) =>
        retryWithExpiredTokenRefresh(
          failureCount,
          error as AxiosError,
          queryClient
        ),
    }
  );
};

const useFileUpload = (folderId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      formData,
      onUploadProgress,
      isMobile,
    }: {
      formData: FormData;
      onUploadProgress?: (arg0: ProgressEvent) => void;
      isMobile?: boolean;
    }) =>
      uploadFile({
        accessToken: await getDocVaultAccessToken(queryClient),
        formData,
        onUploadProgress,
        isMobile,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["folders", folderId]);
      },
      retry: (failureCount, error) =>
        retryWithExpiredTokenRefresh(
          failureCount,
          error as AxiosError,
          queryClient
        ),
    }
  );
};

const fetchFileDownloadURL = (file: BoxFile, accessToken: string) => {
  return request<{ url: string }>(
    "GET",
    `/api/docvault/downloadUrl?fileId=${file.id}&fileVersionId=${file.file_version.id}&accessToken=${accessToken}`
  );
};

const useFileDownloadURL = ({ file }: { file: BoxFile }) => {
  const queryClient = useQueryClient();
  return useQuery(
    ["downloadURL", file.id, file.file_version?.id],
    async () =>
      fetchFileDownloadURL(file, await getDocVaultAccessToken(queryClient)),
    {
      enabled: false,
      retry: (failureCount, error) =>
        retryWithExpiredTokenRefresh(
          failureCount,
          error as AxiosError,
          queryClient
        ),
    }
  );
};

const useFileRename = ({ file }: { file: BoxFile }) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (name: string) =>
      renameFile({
        accessToken: await getDocVaultAccessToken(queryClient),
        file,
        name,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["folders", file.parent.id]);
      },
      retry: (failureCount, error) =>
        retryWithExpiredTokenRefresh(
          failureCount,
          error as AxiosError,
          queryClient
        ),
    }
  );
};

const useFileDelete = ({ file }: { file: BoxFile }) => {
  const queryClient = useQueryClient();
  return useMutation(
    async () =>
      deleteFile({
        accessToken: await getDocVaultAccessToken(queryClient),
        file,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["folders", file.parent.id]);
      },
      retry: (failureCount, error) =>
        retryWithExpiredTokenRefresh(
          failureCount,
          error as AxiosError,
          queryClient
        ),
    }
  );
};

const useFileDeleteById = ({ folderId }: { folderId?: string } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (fileId: string) =>
      deleteFileById({
        accessToken: await getDocVaultAccessToken(queryClient),
        fileId,
      }),
    {
      onSuccess: () => {
        if (folderId) {
          queryClient.invalidateQueries(["folders", folderId]);
        }
      },
      retry: (failureCount, error) =>
        retryWithExpiredTokenRefresh(
          failureCount,
          error as AxiosError,
          queryClient
        ),
    }
  );
};

const useFileMove = ({ file }: { file: BoxFile }) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ folderId }: { folderId: BoxFolder["id"] }) =>
      moveFile({
        accessToken: await getDocVaultAccessToken(queryClient),
        file,
        folderId,
      }),
    {
      onSuccess: () => {
        // if you want to refresh target folder... result.data.parent.id
        queryClient.invalidateQueries(["folders", file.parent.id]);
      },
      retry: (failureCount, error) =>
        retryWithExpiredTokenRefresh(
          failureCount,
          error as AxiosError,
          queryClient
        ),
    }
  );
};

export {
  fetchFileDownloadURL,
  getDocVaultAccessToken,
  useFileRename,
  useFileDelete,
  useFileDeleteById,
  useFileDownloadURL,
  useFileUpload,
  useFileMove,
  useDocVaultJwt,
  useFolder,
};
